import { Sentry } from '@/lib/sentry';

interface FallbackProps {
  error: Error;
  eventId: string | null;
  resetError: () => void;
}

function ErrorFallback({ eventId, resetError }: FallbackProps) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="flex flex-col items-center gap-6 text-center max-w-md">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center overflow-hidden shadow-lg">
          <img
            src="/blazepos-logo.png"
            alt="BlazePOS"
            className="w-16 h-16 object-contain"
          />
        </div>

        <div className="flex flex-col gap-2">
          <h1 className="text-2xl font-bold">Something went wrong</h1>
          <p className="text-muted-foreground text-sm">
            An unexpected error occurred. Your data has been preserved and our
            team has been automatically notified.
          </p>
        </div>

        {eventId && (
          <div className="w-full p-3 rounded-lg bg-muted text-left">
            <p className="text-xs text-muted-foreground font-medium uppercase tracking-wide mb-1">
              Error ID (for support)
            </p>
            <code className="text-xs font-mono text-foreground break-all">
              {eventId}
            </code>
          </div>
        )}

        <div className="flex flex-col gap-3 w-full">
          <button
            onClick={() => window.location.reload()}
            className="w-full px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            Refresh Page
          </button>
          <button
            onClick={resetError}
            className="w-full px-4 py-2 rounded-md border border-border text-sm font-medium hover:bg-muted transition-colors"
          >
            Try to Continue
          </button>
        </div>
      </div>
    </div>
  );
}

export function AppErrorBoundary({ children }: { children: React.ReactNode }) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ error, eventId, resetError }) => (
        <ErrorFallback
          error={error as Error}
          eventId={eventId}
          resetError={resetError}
        />
      )}
      showDialog={false}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
}
